import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, FilePlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ── Option lists ──────────────────────────────────────────────────────────────
// Derived from POC main RowDetailDrawer.js lines 57–98.

const ORDER_STATUS_OPTIONS = [
  'New',
  'Pending Activation',
  'Active',
  'Suspended',
  'Cancelled',
];

const CONTRACT_TYPE_OPTIONS = ['New', 'MACD', 'Renewal', 'Upgrade'];

const CONTRACT_TERM_OPTIONS = [
  'Month-to-Month',
  '12 Months',
  '24 Months',
  '36 Months',
  '60 Months',
];

// <input type="date"> only accepts YYYY-MM-DD; rows carry ISO dates that may
// have a time suffix ("2026-05-01T00:00:00Z") or be blank.
function toDateInput(v) {
  const s = String(v ?? '');
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

// ── CreateNewOrderModal ───────────────────────────────────────────────────────
//
// Pops when the reviewer clicks "Create New Order" in the row drawer.
// Pre-filled from `draft` (sourced from row.output.order_details +
// row.output.order_attributes). The reviewer can edit all fields before
// submitting to AppDirect.
//
// Props:
//   open              {boolean}
//   onOpenChange      {(boolean) => void}
//   draft             {object}  — initial field values
//   submitting        {boolean} — disables submit while parent is saving
//   onConfirm         {(editedDraft) => Promise<void>}
//   salesReps         {{advisor, items}|null} — from drawer cache
//   advisorCompany    {string} — for lazy-fetch trigger
//   onFetchSalesReps  {(advisorName: string) => void}
//   providerProducts  {[{id, name}]} — the row's product options (combobox)
//   catalogWarning    {string|null} — truthful reason the catalog is empty
//                     (auth failure / fetch error), shown in place of the
//                     "no products" placeholder when set
//
// `editedDraft` gains sales_rep_id, sales_rep_name, product_id alongside
// existing fields.

export default function CreateNewOrderModal({
  open,
  onOpenChange,
  draft,
  submitting,
  onConfirm,
  salesReps = null,
  advisorCompany = '',
  onFetchSalesReps,
  providerProducts = [],
  catalogWarning = null,
}) {
  const [fields, setFields] = useState(() => flattenDraft(draft));
  const [repPopoverOpen, setRepPopoverOpen] = useState(false);
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setFields(flattenDraft(draft));
    }
  }, [open, draft]);

  function flattenDraft(d) {
    if (!d) {
      return {
        provider: '',
        product: '',
        product_id: null,
        order_status: '',
        commission_ban: '',
        provider_order_id: '',
        contract_type: '',
        contract_term: '',
        contract_signed: '',
        contract_start: '',
        mrgmv: '',
        nrc: '',
        sales_rep_name: '',
        sales_rep_id: null,
      };
    }
    // draft may come from order_details directly or from the merged output
    // object. Read BOTH path spellings — hand-built templates write
    // contract_signed/contract_start/contract_term, Magic-Mapped ones write
    // contract_signed_date/contract_start_date/contract_term_months; reading
    // only the former left the modal blank while the comparison screen was
    // full (live 2026-07-10).
    return {
      provider: d.provider ?? '',
      product: d.product ?? '',
      product_id: null,
      order_status: d.order_status ?? '',
      commission_ban: d.commission_ban ?? '',
      provider_order_id: d.provider_order_id ?? '',
      contract_type: d.contract_type ?? '',
      contract_term: d.contract_term ?? d.contract_term_months ?? '',
      contract_signed: toDateInput(d.contract_signed ?? d.contract_signed_date),
      contract_start: toDateInput(d.contract_start ?? d.contract_start_date),
      mrgmv: d.mrgmv != null ? String(d.mrgmv) : '',
      nrc: d.nrc != null ? String(d.nrc) : '',
      sales_rep_name: '',
      sales_rep_id: null,
    };
  }

  // Trigger lazy rep fetch when the modal opens and advisor is known
  useEffect(() => {
    if (open && advisorCompany && salesReps === null && onFetchSalesReps) {
      onFetchSalesReps(advisorCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, advisorCompany]);

  function set(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  function handleCancel() {
    onOpenChange(false);
  }

  async function handleSubmit() {
    const payload = {
      ...fields,
      mrgmv: fields.mrgmv !== '' ? parseFloat(fields.mrgmv) : null,
      nrc: fields.nrc !== '' ? parseFloat(fields.nrc) : null,
    };
    await onConfirm(payload);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[560px] bg-boss-surface border border-boss-border text-boss-text"
        data-testid="create-order-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-boss-text">
            <FilePlus className="h-5 w-5 text-boss-text-secondary" />
            Create New Order
          </DialogTitle>
          <DialogDescription className="text-boss-text-secondary">
            Pre-filled from the comparison screen. Edit any field before submitting — these
            values will be sent to create the order in AppDirect.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-4 py-2">
          {/* Provider — locked to the row's provider, shown read-only */}
          <div>
            <Label htmlFor="com-provider" className="text-xs text-boss-text-secondary mb-1.5 block">
              Provider
            </Label>
            <Input
              id="com-provider"
              className="boss-input opacity-70 cursor-not-allowed"
              value={fields.provider}
              readOnly
              disabled
              data-testid="create-order-provider-input"
            />
          </div>

          {/* Product — combobox over the row's provider products. Free text
              here produced productId 0 → pre-flight 422 (raw reports carry
              no product FK); picking from the list is the only value that
              actually books. Falls back to free text when the row has no
              product options. */}
          <div>
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Product
            </Label>
            {providerProducts.length > 0 ? (
              <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={productPopoverOpen}
                    data-testid="create-order-product-input"
                    className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!fields.product ? 'text-boss-text-muted' : ''} ${fields.product && !fields.product_id && !providerProducts.some((p) => p.name === fields.product) ? 'border-[var(--boss-warning-border)] text-[var(--boss-warning-text)] italic' : ''}`}
                  >
                    <span className="truncate">{fields.product || 'Pick a product…'}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[360px] p-0 bg-boss-surface border border-boss-border text-boss-text"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      placeholder="Search products…"
                      className="h-9 text-sm text-boss-text bg-boss-surface"
                    />
                    <CommandList>
                      <CommandEmpty className="py-4 text-center text-sm text-boss-text-muted">
                        No products match.
                      </CommandEmpty>
                      <CommandGroup>
                        {providerProducts.map((p) => (
                          <CommandItem
                            key={p.id}
                            value={p.name}
                            onSelect={() => {
                              set('product', p.name);
                              set('product_id', p.id);
                              setProductPopoverOpen(false);
                            }}
                            className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated"
                          >
                            <Check
                              className={`mr-2 h-4 w-4 shrink-0 ${fields.product_id === p.id ? 'opacity-100' : 'opacity-0'}`}
                            />
                            <span className="truncate">{p.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="space-y-1.5">
                <Input
                  id="com-product"
                  className="boss-input"
                  value={fields.product}
                  onChange={(e) => set('product', e.target.value)}
                  placeholder={catalogWarning ? 'Enter product manually…' : 'No products for this provider'}
                  data-testid="create-order-product-input"
                />
                {catalogWarning && (
                  <p className="text-xs text-[var(--boss-warning-text)] italic" data-testid="create-order-catalog-warning">
                    {catalogWarning}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Sales Rep — combobox or free-text fallback */}
          <div className="col-span-2">
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Sales Rep
            </Label>
            {salesReps && salesReps.items.length > 0 ? (
              <Popover open={repPopoverOpen} onOpenChange={setRepPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={repPopoverOpen}
                    data-testid="create-order-sales-rep-input"
                    className={`w-full flex items-center justify-between rounded-md border border-boss-border px-3 py-2 text-sm text-left boss-input ${!fields.sales_rep_name ? 'text-boss-text-muted' : ''}`}
                  >
                    <span className="truncate">
                      {fields.sales_rep_name || 'Search reps…'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-boss-text-muted" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[320px] p-0 bg-boss-surface border border-boss-border text-boss-text"
                  align="start"
                >
                  <Command>
                    <CommandInput
                      placeholder="Search reps…"
                      className="h-9 text-sm text-boss-text bg-boss-surface"
                    />
                    <CommandList>
                      <CommandEmpty className="py-4 text-center text-sm text-boss-text-muted">
                        No reps found.
                      </CommandEmpty>
                      <CommandGroup>
                        {salesReps.items.map((rep) => (
                          <CommandItem
                            key={rep.id}
                            value={rep.name}
                            onSelect={() => {
                              set('sales_rep_name', rep.name);
                              set('sales_rep_id', rep.id);
                              setRepPopoverOpen(false);
                            }}
                            className="text-sm text-boss-text data-[selected=true]:bg-boss-elevated flex flex-col items-start gap-0.5 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <Check
                                className={`h-4 w-4 shrink-0 ${fields.sales_rep_id === rep.id ? 'opacity-100' : 'opacity-0'}`}
                              />
                              <span>{rep.name}</span>
                            </div>
                            {rep.email && (
                              <span className="pl-6 text-xs text-boss-text-muted leading-tight">{rep.email}</span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                className="boss-input"
                value={fields.sales_rep_name}
                onChange={(e) => set('sales_rep_name', e.target.value)}
                placeholder={
                  salesReps === null
                    ? 'Loading reps…'
                    : 'No reps found — enter manually or leave blank'
                }
                disabled={salesReps === null}
                data-testid="create-order-sales-rep-input"
              />
            )}
          </div>

          {/* Order Status */}
          <div>
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Order Status
            </Label>
            <Select
              value={fields.order_status}
              onValueChange={(v) => set('order_status', v)}
            >
              <SelectTrigger
                className="boss-input w-full"
                data-testid="create-order-status-trigger"
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="max-h-[min(280px,var(--radix-select-content-available-height))] overflow-y-auto bg-boss-surface border border-boss-border text-boss-text">
                {ORDER_STATUS_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt}
                    value={opt}
                    className="hover:bg-boss-surface-elevated focus:bg-boss-surface-elevated text-boss-text"
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Commission BAN */}
          <div>
            <Label htmlFor="com-ban" className="text-xs text-boss-text-secondary mb-1.5 block">
              Commission BAN
            </Label>
            <Input
              id="com-ban"
              className="boss-input"
              value={fields.commission_ban}
              onChange={(e) => set('commission_ban', e.target.value)}
              data-testid="create-order-commission-ban-input"
            />
          </div>

          {/* Provider Order # — the PROVIDER's own order number from the
              report (e.g. a Nextiva order id). NOT the Provider Sales (PS)
              order number — that doesn't exist yet; AppDirect assigns it
              when this order is created. */}
          <div className="col-span-2">
            <Label htmlFor="com-provider-order-id" className="text-xs text-boss-text-secondary mb-1.5 block">
              Provider Order # <span className="normal-case text-boss-text-muted">(the provider's own order number, if the report has one)</span>
            </Label>
            <Input
              id="com-provider-order-id"
              className="boss-input"
              value={fields.provider_order_id}
              onChange={(e) => set('provider_order_id', e.target.value)}
              placeholder="Optional — the PS order number is assigned by AppDirect on create"
              data-testid="create-order-provider-order-id-input"
            />
          </div>

          {/* Contract Type */}
          <div>
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Contract Type
            </Label>
            <Select
              value={fields.contract_type}
              onValueChange={(v) => set('contract_type', v)}
            >
              <SelectTrigger
                className="boss-input w-full"
                data-testid="create-order-contract-type-trigger"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="max-h-[min(280px,var(--radix-select-content-available-height))] overflow-y-auto bg-boss-surface border border-boss-border text-boss-text">
                {CONTRACT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt}
                    value={opt}
                    className="hover:bg-boss-surface-elevated focus:bg-boss-surface-elevated text-boss-text"
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contract Term */}
          <div>
            <Label className="text-xs text-boss-text-secondary mb-1.5 block">
              Contract Term
            </Label>
            <Select
              value={fields.contract_term}
              onValueChange={(v) => set('contract_term', v)}
            >
              <SelectTrigger
                className="boss-input w-full"
                data-testid="create-order-contract-term-trigger"
              >
                <SelectValue placeholder="Select term" />
              </SelectTrigger>
              <SelectContent className="max-h-[min(280px,var(--radix-select-content-available-height))] overflow-y-auto bg-boss-surface border border-boss-border text-boss-text">
                {CONTRACT_TERM_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt}
                    value={opt}
                    className="hover:bg-boss-surface-elevated focus:bg-boss-surface-elevated text-boss-text"
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contract Signed */}
          <div>
            <Label htmlFor="com-contract-signed" className="text-xs text-boss-text-secondary mb-1.5 block">
              Contract Signed
            </Label>
            <Input
              id="com-contract-signed"
              type="date"
              className="boss-input"
              value={fields.contract_signed}
              onChange={(e) => set('contract_signed', e.target.value)}
              data-testid="create-order-contract-signed-input"
            />
          </div>

          {/* Contract Start — AppDirect rejects a dates-less order line, so
              the modal must carry the date the row already has. */}
          <div>
            <Label htmlFor="com-contract-start" className="text-xs text-boss-text-secondary mb-1.5 block">
              Contract Start
            </Label>
            <Input
              id="com-contract-start"
              type="date"
              className="boss-input"
              value={fields.contract_start}
              onChange={(e) => set('contract_start', e.target.value)}
              data-testid="create-order-contract-start-input"
            />
          </div>

          {/* MRGMV */}
          <div>
            <Label htmlFor="com-mrgmv" className="text-xs text-boss-text-secondary mb-1.5 block">
              MRGMV
            </Label>
            <Input
              id="com-mrgmv"
              type="number"
              step="0.01"
              min="0"
              className="boss-input"
              value={fields.mrgmv}
              onChange={(e) => set('mrgmv', e.target.value)}
              placeholder="0.00"
              data-testid="create-order-mrgmv-input"
            />
          </div>

          {/* NRC */}
          <div>
            <Label htmlFor="com-nrc" className="text-xs text-boss-text-secondary mb-1.5 block">
              NRC
            </Label>
            <Input
              id="com-nrc"
              type="number"
              step="0.01"
              min="0"
              className="boss-input"
              value={fields.nrc}
              onChange={(e) => set('nrc', e.target.value)}
              placeholder="0.00"
              data-testid="create-order-nrc-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
            className="border-boss-border text-boss-text-secondary hover:text-boss-text hover:bg-boss-elevated"
            data-testid="create-order-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex items-center gap-1.5"
            data-testid="create-order-submit-button"
          >
            <FilePlus className="h-4 w-4" />
            {submitting ? 'Creating…' : 'Create Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
